<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();
const alert = useAlert;

const content = ref('');
const contactPhone = ref('');
const contactSearch = ref('');
const selectedContact = ref(null);
const inboxId = ref('');
const scheduledAt = ref('');
const isSubmitting = ref(false);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);

const MESSAGE_MAX_LENGTH = 10000;

const contentLength = computed(() => content.value.length);
const isValid = computed(() => {
  return content.value.trim() && scheduledAt.value && inboxId.value && (selectedContact.value || contactPhone.value.trim());
});

const datetimeLocalValue = computed({
  get() {
    if (!scheduledAt.value) return '';
    const d = new Date(scheduledAt.value);
    if (isNaN(d.getTime())) return '';
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  },
  set(val) {
    if (val) {
      scheduledAt.value = new Date(val).toISOString();
    } else {
      scheduledAt.value = '';
    }
  },
});

const quickScheduleOptions = [
  { label: 'Em 1 Hora', hours: 1 },
  { label: 'Em 3 Horas', hours: 3 },
  { label: 'Amanha 9h', hours: null },
  { label: 'Proxima Segunda 9h', hours: null },
];

const setQuickSchedule = option => {
  if (option.hours) {
    const d = new Date();
    d.setHours(d.getHours() + option.hours);
    scheduledAt.value = d.toISOString();
  } else if (option.label.includes('Amanha')) {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    scheduledAt.value = d.toISOString();
  } else if (option.label.includes('Segunda')) {
    const d = new Date();
    const daysUntilMonday = (8 - d.getDay()) % 7 || 7;
    d.setDate(d.getDate() + daysUntilMonday);
    d.setHours(9, 0, 0, 0);
    scheduledAt.value = d.toISOString();
  }
};

const handleSubmit = async () => {
  if (!isValid.value || isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    const conversationId = selectedContact.value?.conversationId || null;

    if (!conversationId && !contactPhone.value.trim()) {
      alert(t('SCHEDULED_MESSAGES_PAGE.ERRORS.CONTACT_REQUIRED'));
      isSubmitting.value = false;
      return;
    }

    await store.dispatch('accountScheduledMessages/create', {
      payload: {
        conversation_id: conversationId,
        content: content.value.trim(),
        scheduled_at: scheduledAt.value,
        contact_phone: contactPhone.value.trim(),
        inbox_id: inboxId.value,
      },
    });

    alert(t('SCHEDULED_MESSAGES.CREATED_SUCCESS'));
    content.value = '';
    contactPhone.value = '';
    selectedContact.value = null;
    scheduledAt.value = '';
    emit('close');
    store.dispatch('accountScheduledMessages/getAll');
  } catch {
    alert(t('SCHEDULED_MESSAGES.ERRORS.SAVE_FAILED'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="space-y-4">
    <h3 class="text-sm font-medium text-n-slate-12">
      {{ t('SCHEDULED_MESSAGES_PAGE.CREATE_FORM.TITLE') }}
    </h3>

    <!-- Contact Input -->
    <div class="space-y-2">
      <label class="block text-sm text-n-text-light dark:text-n-text-muted">
        {{ t('SCHEDULED_MESSAGES_PAGE.CREATE_FORM.CONTACT') }}
      </label>
      <div class="flex gap-2">
        <input
          v-model="contactPhone"
          type="tel"
          placeholder="5535999999999"
          class="flex-1 text-sm border rounded-lg px-3 py-2 bg-white dark:bg-n-surface-2 border-n-divider dark:border-n-divider text-n-slate-12 placeholder-n-text-light dark:placeholder-n-text-muted focus:outline-none focus:ring-2 focus:ring-n-primary-300"
        />
      </div>
    </div>

    <!-- Message Content -->
    <div class="space-y-2">
      <label class="block text-sm text-n-text-light dark:text-n-text-muted">Caixa de envio</label>
      <select v-model="inboxId" class="w-full text-sm border rounded-lg px-3 py-2 bg-white dark:bg-n-surface-2 border-n-divider dark:border-n-divider text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-300">
        <option value="" disabled>Selecione a caixa</option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">{{ inbox.name }}</option>
      </select>
    </div>

    <!-- Message Content -->
    <div class="space-y-2">
      <div class="flex items-center justify-between">
        <label class="block text-sm text-n-text-light dark:text-n-text-muted">
          {{ t('SCHEDULED_MESSAGES_PAGE.CREATE_FORM.MESSAGE') }}
        </label>
        <span class="text-xs text-n-text-light dark:text-n-text-muted">
          {{ contentLength }}/{{ MESSAGE_MAX_LENGTH }}
        </span>
      </div>
      <textarea
        v-model="content"
        :maxlength="MESSAGE_MAX_LENGTH"
        rows="4"
        class="w-full text-sm border rounded-lg px-3 py-2 bg-white dark:bg-n-surface-2 border-n-divider dark:border-n-divider text-n-slate-12 placeholder-n-text-light dark:placeholder-n-text-muted focus:outline-none focus:ring-2 focus:ring-n-primary-300 resize-none"
        :placeholder="t('SCHEDULED_MESSAGES_PAGE.CREATE_FORM.MESSAGE_PLACEHOLDER')"
      />
    </div>

    <!-- Date/Time -->
    <div class="space-y-2">
      <label class="block text-sm text-n-text-light dark:text-n-text-muted">
        {{ t('SCHEDULED_MESSAGES_PAGE.CREATE_FORM.SCHEDULE_AT') }}
      </label>
      <input
        v-model="datetimeLocalValue"
        type="datetime-local"
        class="w-full text-sm border rounded-lg px-3 py-2 bg-white dark:bg-n-surface-2 border-n-divider dark:border-n-divider text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-300"
      />
      <div class="flex flex-wrap gap-2">
        <button
          v-for="option in quickScheduleOptions"
          :key="option.label"
          type="button"
          class="px-3 py-1 text-xs rounded-full border border-n-divider dark:border-n-divider text-n-text-light dark:text-n-text-muted hover:bg-n-surface-3 dark:hover:bg-n-surface-3 transition-colors"
          @click="setQuickSchedule(option)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-end gap-3 pt-2">
      <Button
        :label="t('SCHEDULED_MESSAGES.DISCARD')"
        variant="secondary"
        size="sm"
        @click="emit('close')"
      />
      <Button
        :label="t('SCHEDULED_MESSAGES_PAGE.CREATE_FORM.SCHEDULE')"
        :disabled="!isValid || isSubmitting"
        :loading="isSubmitting"
        size="sm"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
